import React from 'react';
import styled from '@emotion/styled';
import ResultsGrid from './ResultsGrid';
import LoadingSpinner from './LoadingSpinner';
import { colors } from '../theme/colors';

const ResultsContainer = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
`;

const ResultsContent = styled.div`
  background: white;
  border-radius: 4px;
  padding: 0;
  margin: -1rem; /* Offset the parent padding */
`;

const NoResults = styled.div`
  text-align: center;
  color: #6c757d;
  padding: 2rem;
  font-style: italic;
`;

const Button = styled.button`
  background: ${props => props.primary ? '#3b82f6' : props.secondary ? '#6b7280' : '#3b82f6'};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.primary ? '#2563eb' : props.secondary ? '#4b5563' : '#2563eb'};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SaveButton = styled(Button)`
  background: ${colors.successLight};
  
  &:hover {
    background: ${colors.successDark};
  }
  
  &:disabled {
    background: #9ca3af;
  }
`;

const ClearButton = styled.button`
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
    color: #374151;
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
    border-color: #e5e7eb;
    cursor: not-allowed;
  }
`;

function ResultsDisplay({ extractionResults, analysisResults, actionButtons, loanId }) {
  // Determine which results to show (analysis takes priority over extraction)
  const resultsToShow = analysisResults && Object.keys(analysisResults).length > 0 
    ? analysisResults 
    : extractionResults;

  const hasResults = resultsToShow && Object.keys(resultsToShow).length > 0;

  if (!hasResults) {
    return (
      <ResultsContainer>
        <NoResults>
          No results to display
        </NoResults>
      </ResultsContainer>
    );
  }

  // Render action buttons if provided
  const renderActionButtons = () => {
    if (!actionButtons) return null;
    
    return (
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        {actionButtons.extractButton && (
          <Button 
            onClick={actionButtons.extractButton.onClick}
            disabled={actionButtons.extractButton.disabled}
            primary={actionButtons.extractButton.primary}
          >
            {actionButtons.extractButton.loading && <LoadingSpinner size="small" />}
            {actionButtons.extractButton.text}
          </Button>
        )}
        {actionButtons.analyzeButton && (
          <Button 
            onClick={actionButtons.analyzeButton.onClick}
            disabled={actionButtons.analyzeButton.disabled}
            secondary={actionButtons.analyzeButton.secondary}
          >
            {actionButtons.analyzeButton.loading && <LoadingSpinner size="small" />}
            {actionButtons.analyzeButton.text}
          </Button>
        )}
        {actionButtons.saveButton && (
          <SaveButton 
            onClick={actionButtons.saveButton.onClick}
            disabled={actionButtons.saveButton.disabled}
          >
            {actionButtons.saveButton.loading && <LoadingSpinner size="small" />}
            {actionButtons.saveButton.text}
          </SaveButton>
        )}
        {actionButtons.clearButton && (
          <ClearButton onClick={actionButtons.clearButton.onClick}>
            {actionButtons.clearButton.text}
          </ClearButton>
        )}
      </div>
    );
  };


  return (
    <ResultsContainer data-cy="extraction-results-tiles">
      <ResultsContent>
        <ResultsGrid 
          results={resultsToShow} 
          loanId={loanId || resultsToShow._metadata?.loan_id || resultsToShow._metadata?.loanId || 'Unknown'}
        />
      </ResultsContent>
    </ResultsContainer>
  );
}

export default ResultsDisplay;
